import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import { EditSaveCancel } from '@/components/ui/edit-save-cancel';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import type {
    MfaResetRequest,
    MfaResetResponse,
    UserProfileDetailedPublicResponse,
} from '@pillar-portal/api-types';
import {
    PasswordPolicyFeedback,
    passwordPolicyBlocksSubmit,
    usePasswordPolicyFeedback,
} from '@/components/auth/PasswordPolicyFeedback';

export interface UserPasswordCardUser {
    first_name: UserProfileDetailedPublicResponse['first_name'];
    last_name: UserProfileDetailedPublicResponse['last_name'];
    primary_email: UserProfileDetailedPublicResponse['primary_email'];
    emails: UserProfileDetailedPublicResponse['emails'];
    totp_enabled: UserProfileDetailedPublicResponse['totp_enabled'];
    mfa_required: UserProfileDetailedPublicResponse['mfa_required'];
    mfa_email_allowed: UserProfileDetailedPublicResponse['mfa_email_allowed'];
    mfa_sms_allowed: UserProfileDetailedPublicResponse['mfa_sms_allowed'];
}

export interface UserPasswordCardProps {
    user: UserPasswordCardUser;
    onSetPassword: (newPassword: string) => void;
    onRequestPasswordReset: (email: string) => Promise<void>;
    isSettingPassword: boolean;
    setPasswordError: Error | null;
    didSetPasswordSucceed: boolean;
    isRequestingPasswordReset: boolean;
    passwordResetRequestError: Error | null;
    passwordResetRequestMessage: string | null;
    canResetMfa: boolean;
    onResetMfa: (request: MfaResetRequest) => Promise<void>;
    isResettingMfa: boolean;
    mfaResetError: Error | null;
    mfaResetResponse: MfaResetResponse | null;
    canResetEmailAuthenticationAttemptLimit: boolean;
    onResetEmailAuthenticationAttemptLimit: () => Promise<void>;
    isResettingEmailAuthenticationAttemptLimit: boolean;
    emailAuthenticationAttemptLimitResetError: Error | null;
    emailAuthenticationAttemptLimitResetSucceeded: boolean;
    canManageMfaRequired: boolean;
    onSetMfaRequired: (required: boolean) => Promise<void>;
    isSettingMfaRequired: boolean;
    mfaRequiredError: Error | null;
    mfaRequiredMessage: string | null;
}

export function getUserPasswordResetDisplayName(user: UserPasswordCardUser): string {
    const fullName: string = [user.first_name ?? '', user.last_name ?? '']
        .map((value) => value.trim())
        .filter((value) => value.length > 0)
        .join(' ');

    if (fullName.length > 0) {
        return fullName;
    }

    return user.primary_email;
}

export function getUserPasswordResetEmails(user: UserPasswordCardUser): string[] {
    const emails = new Set<string>([user.primary_email, ...user.emails]);
    return Array.from(emails);
}

export function UserPasswordCard(props: UserPasswordCardProps): ReactElement {
    const [isEditingPassword, setIsEditingPassword] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isPasswordResetDialogOpen, setIsPasswordResetDialogOpen] = useState(false);
    const [isMfaRequirementDialogOpen, setIsMfaRequirementDialogOpen] = useState(false);
    const [isMfaResetDialogOpen, setIsMfaResetDialogOpen] = useState(false);
    const [
        isEmailAuthenticationAttemptLimitResetDialogOpen,
        setIsEmailAuthenticationAttemptLimitResetDialogOpen,
    ] = useState(false);
    const [mfaResetReason, setMfaResetReason] = useState('');
    const [isIdentityVerificationAttested, setIsIdentityVerificationAttested] = useState(false);
    const [hasSubmittedPassword, setHasSubmittedPassword] = useState(false);
    const previousDidSetPasswordSucceed = useRef(props.didSetPasswordSucceed);
    const passwordPolicyState = usePasswordPolicyFeedback(newPassword);

    const passwordResetEmails: string[] = useMemo(
        () => getUserPasswordResetEmails(props.user),
        [props.user]
    );
    const [selectedPasswordResetEmail, setSelectedPasswordResetEmail] = useState<string>(
        props.user.primary_email
    );

    const setPasswordDisabled: boolean =
        !newPassword ||
        !confirmPassword ||
        newPassword !== confirmPassword ||
        passwordPolicyBlocksSubmit(passwordPolicyState, newPassword);
    const passwordResetDisplayName: string = getUserPasswordResetDisplayName(props.user);
    const hasActiveMfa: boolean =
        props.user.totp_enabled || props.user.mfa_email_allowed || props.user.mfa_sms_allowed;
    const canUseMfaRequirementAction: boolean =
        props.canManageMfaRequired && hasActiveMfa && !props.isSettingMfaRequired;
    const nextMfaRequiredState: boolean = !props.user.mfa_required;
    const mfaRequirementActionLabel: string = props.user.mfa_required
        ? 'Disable MFA'
        : 'Enable MFA';
    const canSubmitMfaReset: boolean =
        mfaResetReason.trim().length > 0 && isIdentityVerificationAttested && !props.isResettingMfa;

    const handleConfirmMfaReset = async (): Promise<void> => {
        await props.onResetMfa({
            reason: mfaResetReason,
            identity_verification_attested: isIdentityVerificationAttested,
        });
        setIsMfaResetDialogOpen(false);
        setMfaResetReason('');
        setIsIdentityVerificationAttested(false);
    };

    const handleCancelPasswordEdit = (): void => {
        setIsEditingPassword(false);
        setNewPassword('');
        setConfirmPassword('');
        setHasSubmittedPassword(false);
    };

    const handleConfirmPasswordReset = async (): Promise<void> => {
        await props.onRequestPasswordReset(selectedPasswordResetEmail);
        setIsPasswordResetDialogOpen(false);
    };

    useEffect(() => {
        const didSetPasswordJustSucceed: boolean =
            !previousDidSetPasswordSucceed.current && props.didSetPasswordSucceed;
        previousDidSetPasswordSucceed.current = props.didSetPasswordSucceed;

        if (hasSubmittedPassword && didSetPasswordJustSucceed) {
            setIsEditingPassword(false);
            setNewPassword('');
            setConfirmPassword('');
            setHasSubmittedPassword(false);
        }
    }, [hasSubmittedPassword, props.didSetPasswordSucceed]);

    useEffect(() => {
        if (props.setPasswordError !== null) {
            setHasSubmittedPassword(false);
        }
    }, [props.setPasswordError]);

    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Authentication</CardTitle>
                <div className="flex gap-2">
                    {props.canResetEmailAuthenticationAttemptLimit && !isEditingPassword ? (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                                setIsEmailAuthenticationAttemptLimitResetDialogOpen(true)
                            }
                            disabled={props.isResettingEmailAuthenticationAttemptLimit}
                        >
                            Reset Email Sign-In Limit
                        </Button>
                    ) : null}
                    {props.canResetMfa && !isEditingPassword ? (
                        <Button
                            id="mfaResetButton"
                            size="sm"
                            variant="destructive"
                            onClick={() => setIsMfaResetDialogOpen(true)}
                            disabled={props.isResettingMfa}
                        >
                            Reset MFA
                        </Button>
                    ) : null}
                    {props.canManageMfaRequired && !isEditingPassword ? (
                        <Button
                            size="sm"
                            variant={props.user.mfa_required ? 'destructive' : 'outline'}
                            onClick={() => setIsMfaRequirementDialogOpen(true)}
                            disabled={!canUseMfaRequirementAction}
                            title={
                                !hasActiveMfa
                                    ? 'This user must configure at least one MFA method before MFA can be required.'
                                    : undefined
                            }
                        >
                            {mfaRequirementActionLabel}
                        </Button>
                    ) : null}
                    {!isEditingPassword ? (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                                setSelectedPasswordResetEmail(props.user.primary_email);
                                setIsPasswordResetDialogOpen(true);
                            }}
                            disabled={props.isRequestingPasswordReset}
                        >
                            Password Reset
                        </Button>
                    ) : null}
                    <EditSaveCancel
                        onEdit={() => setIsEditingPassword(true)}
                        onSave={() => {
                            if (!setPasswordDisabled) {
                                setHasSubmittedPassword(true);
                                props.onSetPassword(newPassword);
                            }
                        }}
                        onCancel={handleCancelPasswordEdit}
                        isLoading={props.isSettingPassword}
                        editButtonName="Set Password"
                        isEditing={isEditingPassword}
                    />
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                {isEditingPassword ? (
                    <>
                        <div className="space-y-2">
                            <Label htmlFor="resetPassword">New Password</Label>
                            <Input
                                id="resetPassword"
                                type="password"
                                placeholder="Enter new password"
                                value={newPassword}
                                onChange={(event) => setNewPassword(event.target.value)}
                                disabled={props.isSettingPassword}
                            />
                            <PasswordPolicyFeedback
                                password={newPassword}
                                state={passwordPolicyState}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="confirmSetPassword">Confirm Password</Label>
                            <Input
                                id="confirmSetPassword"
                                type="password"
                                placeholder="Confirm new password"
                                value={confirmPassword}
                                onChange={(event) => setConfirmPassword(event.target.value)}
                                disabled={props.isSettingPassword}
                            />
                        </div>
                        {newPassword && confirmPassword && newPassword !== confirmPassword ? (
                            <Alert variant="destructive">
                                <AlertDescription>Passwords do not match</AlertDescription>
                            </Alert>
                        ) : null}
                        {props.setPasswordError !== null ? (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    {props.setPasswordError.message}
                                </AlertDescription>
                            </Alert>
                        ) : null}
                        {props.didSetPasswordSucceed ? (
                            <Alert>
                                <AlertDescription>Password reset successfully!</AlertDescription>
                            </Alert>
                        ) : null}
                    </>
                ) : (
                    <>
                        {props.didSetPasswordSucceed ? (
                            <Alert>
                                <AlertDescription>Password reset successfully!</AlertDescription>
                            </Alert>
                        ) : null}
                        {props.passwordResetRequestError !== null ? (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    {props.passwordResetRequestError.message}
                                </AlertDescription>
                            </Alert>
                        ) : null}
                        {props.passwordResetRequestMessage !== null ? (
                            <Alert>
                                <AlertDescription>
                                    {props.passwordResetRequestMessage}
                                </AlertDescription>
                            </Alert>
                        ) : null}
                        {props.mfaRequiredError !== null ? (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    {props.mfaRequiredError.message}
                                </AlertDescription>
                            </Alert>
                        ) : null}
                        {props.mfaRequiredMessage !== null ? (
                            <Alert>
                                <AlertDescription>{props.mfaRequiredMessage}</AlertDescription>
                            </Alert>
                        ) : null}
                        {props.mfaResetError !== null ? (
                            <Alert variant="destructive">
                                <AlertDescription>{props.mfaResetError.message}</AlertDescription>
                            </Alert>
                        ) : null}
                        {props.mfaResetResponse !== null ? (
                            <Alert>
                                <AlertDescription>
                                    {props.mfaResetResponse.notification_outcome === 'sent'
                                        ? 'MFA reset succeeded and the notification email was sent.'
                                        : props.mfaResetResponse.notification_outcome === 'failed'
                                          ? 'MFA reset succeeded, but notification delivery failed.'
                                          : 'MFA reset succeeded, but no verified primary email is available for notification.'}
                                </AlertDescription>
                            </Alert>
                        ) : null}
                        {props.emailAuthenticationAttemptLimitResetError !== null ? (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    {props.emailAuthenticationAttemptLimitResetError.message}
                                </AlertDescription>
                            </Alert>
                        ) : null}
                        {props.emailAuthenticationAttemptLimitResetSucceeded ? (
                            <Alert>
                                <AlertDescription>
                                    Email sign-in request limit reset successfully.
                                </AlertDescription>
                            </Alert>
                        ) : null}
                    </>
                )}
            </CardContent>
            <ConfirmationModal
                open={isPasswordResetDialogOpen}
                onOpenChange={setIsPasswordResetDialogOpen}
                title="Send Password Reset"
                message={`Do you want to send ${passwordResetDisplayName} a password reset email?`}
                confirmLabel="Send Password Reset"
                isLoading={props.isRequestingPasswordReset}
                onConfirm={() => {
                    void handleConfirmPasswordReset().catch(() => undefined);
                }}
            >
                <div className="space-y-2">
                    <Label htmlFor="passwordResetEmail">Email address</Label>
                    {passwordResetEmails.length > 1 ? (
                        <Select
                            value={selectedPasswordResetEmail}
                            onValueChange={setSelectedPasswordResetEmail}
                        >
                            <SelectTrigger id="passwordResetEmail" aria-label="Email address">
                                <SelectValue placeholder="Select email address" />
                            </SelectTrigger>
                            <SelectContent>
                                {passwordResetEmails.map((email) => (
                                    <SelectItem key={email} value={email}>
                                        {email}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    ) : (
                        <p className="text-sm text-muted-foreground">
                            Email address: {passwordResetEmails[0]}
                        </p>
                    )}
                </div>
            </ConfirmationModal>
            <ConfirmationModal
                open={isEmailAuthenticationAttemptLimitResetDialogOpen}
                onOpenChange={setIsEmailAuthenticationAttemptLimitResetDialogOpen}
                title="Reset Email Sign-In Limit"
                message={`Reset the email sign-in request limit for ${passwordResetDisplayName} (${props.user.primary_email})?`}
                confirmLabel="Reset Email Sign-In Limit"
                isLoading={props.isResettingEmailAuthenticationAttemptLimit}
                onConfirm={() => {
                    void props
                        .onResetEmailAuthenticationAttemptLimit()
                        .then(() => {
                            setIsEmailAuthenticationAttemptLimitResetDialogOpen(false);
                        })
                        .catch(() => undefined);
                }}
            />
            <ConfirmationModal
                open={isMfaResetDialogOpen}
                onOpenChange={setIsMfaResetDialogOpen}
                title="Reset MFA"
                message={`Reset all MFA methods and authenticated sessions for ${passwordResetDisplayName}?`}
                confirmLabel="Reset MFA"
                isLoading={props.isResettingMfa}
                confirmDisabled={!canSubmitMfaReset}
                onConfirm={() => {
                    void handleConfirmMfaReset().catch(() => undefined);
                }}
            >
                <div className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="mfaResetReason">Reason</Label>
                        <Input
                            id="mfaResetReason"
                            value={mfaResetReason}
                            onChange={(event) => setMfaResetReason(event.target.value)}
                            disabled={props.isResettingMfa}
                        />
                    </div>
                    <Label className="flex items-start gap-2" htmlFor="mfaResetAttestation">
                        <Checkbox
                            id="mfaResetAttestation"
                            checked={isIdentityVerificationAttested}
                            onChange={(event) =>
                                setIsIdentityVerificationAttested(event.target.checked)
                            }
                            disabled={props.isResettingMfa}
                        />
                        I attest that I verified the user's identity.
                    </Label>
                </div>
            </ConfirmationModal>
            <ConfirmationModal
                open={isMfaRequirementDialogOpen}
                onOpenChange={setIsMfaRequirementDialogOpen}
                title={mfaRequirementActionLabel}
                message={
                    props.user.mfa_required
                        ? `Disable MFA requirement for ${passwordResetDisplayName}?`
                        : `Require MFA for ${passwordResetDisplayName}?`
                }
                confirmLabel={mfaRequirementActionLabel}
                isLoading={props.isSettingMfaRequired}
                onConfirm={() => {
                    void props
                        .onSetMfaRequired(nextMfaRequiredState)
                        .then(() => {
                            setIsMfaRequirementDialogOpen(false);
                        })
                        .catch(() => undefined);
                }}
            >
                <p className="text-sm text-muted-foreground">
                    {props.user.mfa_required
                        ? 'This stops requiring MFA at login, reducing protection against unauthorized access.'
                        : 'This requires the user to complete one of their configured MFA methods at login.'}
                </p>
            </ConfirmationModal>
        </Card>
    );
}
